export type WriteBackChainRow = {
  id: string;
  s3_key: string | null;
  supersedes: string | null;
  sent_back_at: string | null;
  written_to_s3_at: string | null;
};

const attemptedAt = (row: WriteBackChainRow): number => Date.parse(row.sent_back_at ?? row.written_to_s3_at ?? "") || 0;

const newestFirst = <T extends WriteBackChainRow>(rows: readonly T[]): T[] =>
  rows.slice().sort((a, b) => attemptedAt(b) - attemptedAt(a));

const walkDeliveryChain = <T extends WriteBackChainRow>(rows: readonly T[]): T[] => {
  const delivered = rows.filter((row) => row.s3_key !== null);
  const byId = new Map(delivered.map((row) => [row.id, row]));
  const superseded = new Set(delivered.flatMap((row) => (row.supersedes === null ? [] : [row.supersedes])));
  const heads = delivered.filter((row) => !superseded.has(row.id));

  const chain: T[] = [];
  if (heads.length === 1) {
    const seen = new Set<string>();
    let step: T | undefined = heads[0];
    while (step !== undefined && !seen.has(step.id)) {
      chain.push(step);
      seen.add(step.id);
      step = step.supersedes === null ? undefined : byId.get(step.supersedes);
    }
  }

  return chain;
};

const orderAroundChain = <T extends WriteBackChainRow>(rows: readonly T[], chain: readonly T[]): T[] => {
  const walked = new Set(chain.map((row) => row.id));

  return [...chain, ...newestFirst(rows.filter((row) => !walked.has(row.id)))];
};

export type DeliveryChain<T extends WriteBackChainRow> = {
  /** Head first; empty when the chain is malformed, as a fork or cycle names no head. */
  walked: readonly T[];
  ordered: readonly T[];
};

/** Pass every row for the encounter: a chain crosses classification results, and a slice of one reads as all heads. */
export const readDeliveryChain = <T extends WriteBackChainRow>(rows: readonly T[]): DeliveryChain<T> => {
  const walked = walkDeliveryChain(rows);

  return { walked, ordered: orderAroundChain(rows, walked) };
};

export type OwnWriteBack<T> = {
  latest: T | null;
  /** The encounter's current write-back, whoever owns it. */
  head: T | null;
  isSuperseded: boolean;
  isWrittenBack: boolean;
};

export const resolveOwnWriteBack = <T extends WriteBackChainRow & { status: string }>(
  { walked, ordered }: DeliveryChain<T>,
  isOwn: (row: T) => boolean,
): OwnWriteBack<T> => {
  const latest = ordered.find(isOwn) ?? null;
  const isSuperseded = latest !== null && walked.indexOf(latest) > 0;

  return {
    latest,
    head: walked[0] ?? null,
    isSuperseded,
    isWrittenBack: latest?.status === "success" && !isSuperseded,
  };
};

export const isHeadFromReview = <T extends WriteBackChainRow & { human_review_id: string | null }>({
  walked,
}: DeliveryChain<T>): boolean => (walked[0]?.human_review_id ?? null) !== null;
